import { CardHeader, CardBody, CardFooter, Button } from "@heroui/react";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { ReactComponent as GoogleLogo } from "@wc/ui/assets/google-logo.svg";
import SingleCardOnPage from "@wc/ui/components/single-card-on-page";

import useAuth from "../../hooks/use-auth";

/**
 * An icon button for auth methods
 * @param {object} props The component props
 * @param {React.ReactElement} props.children The body of the button
 * @param {string} [props.returnUrl] URL to navigate to after login
 * @returns {React.ReactElement} The OAuthButton
 */
function OAuthButton({ children, returnUrl }) {
  const navigate = useNavigate();
  const auth = useAuth();
  const [isLoading, setIsLoading] = React.useState(false);

  return (
    <Button
      isIconOnly
      isLoading={isLoading}
      size="lg"
      radius="full"
      onPress={async () => {
        setIsLoading(true);
        const result = await auth.loginOAuth();
        if (result) {
          navigate(returnUrl || "/");
        } else {
          setIsLoading(false);
        }
      }}
    >
      {children}
    </Button>
  );
}

/**
 * @returns {React.ReactElement} A login card
 */
export default function LoginCard() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get("returnUrl");

  return (
    <SingleCardOnPage>
      <CardHeader className="flex justify-center">
        <h1 className="font-bold sm:text-2xl">{t("loginHeader")}</h1>
      </CardHeader>
      <CardBody className="flex flex-row justify-center gap-4">
        <OAuthButton returnUrl={returnUrl}>
          <GoogleLogo role="img" aria-label={"Google"} />
        </OAuthButton>
      </CardBody>
      <CardFooter className="flex justify-center">
        {t("loginFooter")}
      </CardFooter>
    </SingleCardOnPage>
  );
}
